import {
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  DescriptionOutlined,
  DownloadOutlined,
  MoreVertOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import { useState } from "react";
import { useSelector } from "react-redux";

import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import {
  getInitials,
  rateDelta,
  STAGE_TONE,
  trimNum,
  typeTone,
} from "./sizerDisplay";

const ACCENT = green[600];

const Badge = ({ label, tone, filled }) => {
  if (!label) return null;
  const t = tone || { fg: grey[700], bg: grey[100] };
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 600,
        color: filled ? "#fff" : t.fg,
        background: filled ? ACCENT : t.bg,
        border: `1px solid ${filled ? ACCENT : t.bg}`,
      }}
    />
  );
};

// One column in the metrics strip: uppercase label, large value, muted sub-line.
const Stat = ({ label, value, sub, subColor, subIcon, withDivider }) => (
  <Box
    sx={{
      pl: withDivider ? 2.5 : 0,
      borderLeft: withDivider ? `1px solid ${grey[200]}` : "none",
    }}
  >
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: grey[500],
        display: "block",
        mb: 0.75,
      }}
    >
      {label}
    </Txt>
    <Txt
      component="div"
      sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a", lineHeight: 1.15 }}
    >
      {value ?? "—"}
    </Txt>
    {sub && (
      <Flx ac g={0.5} sx={{ mt: 0.5 }}>
        {subIcon}
        <Txt sx={{ fontSize: 12, color: subColor || grey[500] }}>{sub}</Txt>
      </Flx>
    )}
  </Box>
);

/**
 * The "Current" hero card at the top of the Sizer tab — the most recent sizer
 * upload, which is the one that drives loan terms. Shows the file, its Type /
 * Stage badges, upload attribution, and a metrics strip (total loan, rate,
 * points, purpose, stage) with deltas versus the prior upload and a
 * matches-loan-record check.
 */
const SizerCurrentCard = ({ current, prior, total, onUpload }) => {
  const users = useSelector((state) => state.users);
  const loanRecord = useSelector((state) => state.loanDrilldown);
  const { loading, getFileById } = useLoanFilesHook();
  const [menuEl, setMenuEl] = useState(null);

  if (isNil(current)) return null;

  const uploaderName =
    getUserFullNameFromEmail(users, current.uploadedBy) || current.uploadedBy;
  const initials = getInitials(uploaderName) || "—";
  const uploadedStr = current.uploadDate
    ? formatDateTime(current.uploadDate, "MMM d, yyyy · h:mm a")
    : null;

  const onDownload = () => {
    getFileById({
      loanId: current.dealId,
      file_storage_name: current.file_storage_name,
      fileDownloadName: current.file_display_name,
      fileDownload: true,
    });
  };

  // Total-loan snapshot vs. what's on the loan record right now.
  const snapTotal = current.upload_totalLoanAmount;
  const recordTotal = loanRecord?.totalLoanAmount;
  const totalMatches =
    typeof snapTotal === "number" &&
    typeof recordTotal === "number" &&
    Math.round(snapTotal) === Math.round(recordTotal);
  const totalSub = totalMatches
    ? "matches loan record"
    : typeof recordTotal === "number"
      ? `loan record ${formatDollar(recordTotal)}`
      : null;

  // Rate delta vs. the prior upload.
  const delta = rateDelta(current.upload_interestRate, prior?.upload_interestRate);
  let rateSub = null;
  let rateSubColor;
  let rateSubIcon = null;
  if (delta && prior) {
    if (delta.dir === "flat") {
      rateSub = "no change vs. prior";
    } else {
      const up = delta.dir === "up";
      rateSub = `${trimNum(delta.value)} vs. prior`;
      rateSubColor = up ? "#b91c1c" : "#047857";
      rateSubIcon = up ? (
        <ArrowUpwardOutlined sx={{ fontSize: 14, color: rateSubColor }} />
      ) : (
        <ArrowDownwardOutlined sx={{ fontSize: 14, color: rateSubColor }} />
      );
    }
  }

  const origPts = current.upload_originationPoints;
  const discPts = current.upload_discountPoints;

  return (
    <Box
      sx={{
        border: `1px solid ${grey[200]}`,
        borderLeft: `4px solid ${ACCENT}`,
        borderRadius: "12px",
        background: "#fff",
        p: 3,
      }}
    >
      <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
        <Flx g={1.5} sx={{ minWidth: 0, alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "9px",
              background: grey[100],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <DescriptionOutlined className="thin" sx={{ color: grey[600] }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Txt
              component="div"
              sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}
            >
              {current.file_display_name || "Sizer"}
            </Txt>
            <Flx ac wrap g={0.75} sx={{ mt: 0.75, rowGap: 0.5 }}>
              <Badge label="Current" filled />
              <Badge label={current.sizer_type_at_upload} tone={typeTone(current.sizer_type_at_upload)} />
              <Badge
                label={current.sizer_stage_at_upload}
                tone={STAGE_TONE[current.sizer_stage_at_upload]}
              />
              {uploadedStr && (
                <Txt sx={{ fontSize: 12, color: grey[500] }}>
                  {`· Uploaded ${uploadedStr} by ${initials}`}
                </Txt>
              )}
            </Flx>
          </Box>
        </Flx>

        <Flx ac g={0.25} sx={{ flexShrink: 0 }}>
          <Tooltip title="Download">
            <span>
              <IconButton size="small" disabled={loading} onClick={onDownload}>
                <DownloadOutlined className="thin-7" sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)}>
            <MoreVertOutlined sx={{ fontSize: 20 }} />
          </IconButton>
          <Menu
            anchorEl={menuEl}
            open={Boolean(menuEl)}
            onClose={() => setMenuEl(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuEl(null);
                onDownload();
              }}
              sx={{ fontSize: 13, gap: 1 }}
            >
              <DownloadOutlined sx={{ fontSize: 18 }} /> Download
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuEl(null);
                onUpload?.();
              }}
              sx={{ fontSize: 13, gap: 1 }}
            >
              <UploadFileOutlined sx={{ fontSize: 18 }} /> Upload new version
            </MenuItem>
          </Menu>
        </Flx>
      </Flx>

      <Box
        sx={{
          mt: 3,
          pt: 3,
          borderTop: `1px solid ${grey[100]}`,
          display: "grid",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        }}
      >
        <Stat
          label="Total Loan"
          value={formatDollar(snapTotal, "—")}
          sub={totalSub}
          subColor={totalMatches ? "#047857" : grey[500]}
        />
        <Stat
          withDivider
          label="Rate"
          value={
            typeof current.upload_interestRate === "number"
              ? formatPercent(current.upload_interestRate)
              : "—"
          }
          sub={rateSub}
          subColor={rateSubColor}
          subIcon={rateSubIcon}
        />
        <Stat
          withDivider
          label="Origination Pts"
          value={origPts != null ? trimNum(origPts, 5) : "—"}
          sub={discPts != null ? `discount ${trimNum(discPts, 5)}` : null}
        />
        <Stat
          withDivider
          label="Purpose"
          value={current.upload_loanPurpose || "—"}
          sub={current.upload_loanProductType || current.sizer_type_at_upload}
        />
        <Stat
          withDivider
          label="Stage"
          value={current.sizer_stage_at_upload || "—"}
          sub={total > 1 ? `${total} sizers on file` : "most recent upload"}
        />
      </Box>
    </Box>
  );
};

SizerCurrentCard.propTypes = {
  current: PropTypes.object,
  prior: PropTypes.object,
  total: PropTypes.number,
  onUpload: PropTypes.func,
};

export default SizerCurrentCard;
